import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import React, { memo, useEffect, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { setYellowBanner } from '../../ducks/ui.duck';
import { fetchBannerData } from '../../ducks/yellowBanner.duck';
import { yellow } from '../../styles/mui/colors';
import { getClientData, setClientData } from '../../util/clientData';
import useAppSelector from '../../util/hooks/useAppSelector';
import useScreenSize from '../../util/hooks/useScreenSize';
import { isCordova } from '../../util/mobile/deviceDetection';
import LanguageSupport from '../LanguageSupport/LanguageSupport';
import Link from '../Links/Link/Link';

const YellowBannerComponent = ({ currentPage = undefined }) => {
  const dispatch = useDispatch();
  const { isMobile } = useScreenSize();

  // Track banner state
  const yellowBannerState = useAppSelector((state) => state.ui.yellowBannerState);
  const { bannerData, bannerError } = useAppSelector(
    (state) => ({
      bannerData: state.yellowBanner?.bannerData,
      bannerError: state.yellowBanner?.bannerError,
    }),
    shallowEqual,
  );

  const [isBannerClosed, setIsBannerClosed] = useState(
    () => getClientData('vansite.yellowBannerClosed') || false,
  );

  useEffect(() => {
    if (isBannerClosed) {
      dispatch(setYellowBanner(false));
    } else {
      dispatch(fetchBannerData());
    }
  }, [dispatch, isBannerClosed]);

  // Determine if banner should be visible
  const shouldShowBanner = (() => {
    if (!bannerData || bannerError || isBannerClosed) return false;
    const { active, showOnPages } = bannerData;
    if (active === 'false') return false;
    if (!showOnPages.split(',').includes(currentPage) || isCordova()) return false;
    return true;
  })();

  // Sync banner visibility to Redux so dependent components (e.g. SectionHero sticky)
  // can react to layout changes
  useEffect(() => {
    dispatch(setYellowBanner(shouldShowBanner));
  }, [dispatch, shouldShowBanner]);

  const handleClose = () => {
    setClientData('vansite.yellowBannerClosed', true, moment().add(1, 'day').toISOString());
    setIsBannerClosed(true);
  };

  const langCods = ['de'];

  if (!shouldShowBanner) {
    return null;
  }

  const { heading, buttonLabel, buttonSlug, buttonLink } = bannerData;

  return (
    <LanguageSupport languages={langCods}>
      {yellowBannerState ? (
        <Fade in={yellowBannerState}>
          <Box
            id="annual-pass-banner"
            sx={{
              background: yellow.main,
              width: '100%',
              transition: 'transform 0.5s',
              maxHeight: { xs: '100px', md: '44px' },
              position: { md: 'relative' },
            }}
          >
            {isMobile ? (
              <MobileBanner
                buttonLabel={buttonLabel}
                buttonLink={buttonLink}
                buttonSlug={buttonSlug}
                handleClose={handleClose}
                heading={heading}
              />
            ) : (
              <DesktopBanner
                buttonLabel={buttonLabel}
                buttonLink={buttonLink}
                buttonSlug={buttonSlug}
                handleClose={handleClose}
                heading={heading}
              />
            )}
          </Box>
        </Fade>
      ) : null}
    </LanguageSupport>
  );
};

const MobileBanner = ({
  handleClose,
  heading,
  buttonLabel,
  buttonSlug = undefined,
  buttonLink = undefined,
}) => {
  const { isMobile } = useScreenSize();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        p: 1,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography fontSize="0.8em" textAlign="center">
          {heading}
        </Typography>
        <IconButton aria-label="Close annual pass banner" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {buttonLink || buttonSlug ? (
          <Button
            aria-label="Show annual pass page"
            color="secondary"
            component={Link}
            fullWidth={isMobile}
            size="small"
            sx={{
              textAlign: 'center',
              lineHeight: '1.5',
              '&:hover': {
                backgroundColor: 'secondary.main',
                color: 'white',
              },
            }}
            target="_blank"
            to={buttonLink ? buttonLink : { name: 'StoryblokPage', params: { slug: buttonSlug } }}
            variant="contained"
          >
            {buttonLabel}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

const DesktopBanner = ({
  handleClose,
  heading,
  buttonLabel,
  buttonSlug = undefined,
  buttonLink = undefined,
}) => {
  const { isMobile } = useScreenSize();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 0.5,
      }}
    >
      <Typography
        fontSize={{
          xs: '0.8em',
          sm: '0.85em',
          md: '0.90em',
        }}
        textAlign="center"
      >
        {heading}
      </Typography>
      <Button
        aria-label="Show annual pass page"
        color="secondary"
        component={Link}
        fullWidth={isMobile}
        size="small"
        sx={{
          textAlign: 'center',
          lineHeight: '1.5',
          '&:hover': {
            backgroundColor: 'secondary.main',
            color: 'white',
          },
        }}
        target="_blank"
        to={buttonLink ? buttonLink : { name: 'StoryblokPage', params: { slug: buttonSlug } }}
        variant="contained"
      >
        {buttonLabel}
      </Button>
      <IconButton aria-label="Close annual pass banner" onClick={handleClose} size="small">
        <CloseIcon />
      </IconButton>
    </Box>
  );
};

YellowBannerComponent.displayName = 'YellowBanner';

const YellowBanner = memo(YellowBannerComponent);

export default YellowBanner;
